import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDimension } from 'hooks/queryParams/useDimension';
import { CAMPAIGN_SORTABLE_FIELDS } from 'modules/campaigns/config';
import { ENTITY } from 'modules/campaigns/config/routes/entity';
import { ENTITY_TYPE } from 'modules/campaigns/constants/entityType';
import { useEntityPickerType } from 'modules/campaigns/hooks/useEntityPickerType';
import { isCampaignEditFormDisabled } from 'modules/campaigns/modules/campaigns-reports/config/predicates';
import { campaignsMapSelector, campaignsModuleStatusSelector } from 'modules/campaigns/selectors';
import { GTM_EVENTS, gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { getPageName } from 'modules/taboola-common-frontend-modules/gtmTracker/urlPageNameUtil';
import { useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { setCampaignPickerSorting } from '../../actions';
import useOpenCampaignEditor from '../../hooks/useOpenCampaignEditor';
import CampaignPickerListItem from '../CampaignPickerListItem/CampaignPickerListItem';
import { useEntityPickerStateManager } from '../EntityPicker/services/useEntityPickerStateManager';
import { CampaignContentLoader } from './CampaignLoader';
import { EntityPickerLayout } from './EntityPickerLayout';

export const sortFieldsOptions = [
    {
        value: CAMPAIGN_SORTABLE_FIELDS.TOTAL_SPENT,
        label: <FormattedMessage id="campaign.picker.sorting.totalSpent" defaultMessage="Spent (Overall)" />,
    },
    {
        value: CAMPAIGN_SORTABLE_FIELDS.CREATE_DATE,
        label: <FormattedMessage id="campaign.picker.sorting.creationDate" defaultMessage="Creation Date" />,
    },
    {
        value: CAMPAIGN_SORTABLE_FIELDS.NAME,
        label: <FormattedMessage id="campaign.picker.sorting.name" defaultMessage="Name" />,
    },
];

const CampaignPicker = ({
    campaigns,
    loadNextPage,
    totalCampaignsByDimension,
    isItemLoaded,
    paginationStatus,
    sortType,
    sortField,
    debouncedSearchText,

    reset,
    reload,
    forceReloadFlag,
    status,
    setStatus,
    isModuleReady,
    disabled,
    selectedItem,
    searchText,
    setSearchText,

    getItemSize,
    onHeightResolved,
}) => {
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const [selectedCampaignDimension] = useDimension();
    const campaignsModuleStatus = useSelector(campaignsModuleStatusSelector);
    const campaignsFromStore = useSelector(campaignsMapSelector);
    const [selectedPickerType] = useEntityPickerType();

    const pageName = getPageName();
    const basicEditSearchFieldEvent = useMemo(
        () => ({ component: 'CampaignPickerEditSearchField', pageName }),
        [pageName]
    );
    const basicSortEvent = useMemo(() => ({ component: 'CampaignPickerSort', pageName }), [pageName]);

    const handleOnSelectSortField = ({ value: campaignPickerSortingField }) =>
        dispatch(setCampaignPickerSorting({ campaignPickerSortingField }));
    const handleOnSelectSortType = campaignPickerSortingType =>
        dispatch(setCampaignPickerSorting({ campaignPickerSortingType }));
    const sortTooltip = useMemo(() => {
        const sortFieldMessage = formatMessage({ id: `campaign.picker.sorting.${sortField}` });
        const sortTypeMessage = formatMessage({ id: `campaign.picker.sorting.${sortType}` });
        return formatMessage(
            { id: 'campaign.picker.sorting.tooltip' },
            { field: sortFieldMessage, type: sortTypeMessage }
        );
    }, [sortField, sortType, formatMessage]);

    const handleEditCampaign = useOpenCampaignEditor();

    const { reloadWrapper } = useEntityPickerStateManager({
        reset,
        reload,
        setSearchText,
        setStatus,
        moduleStatus: campaignsModuleStatus,
        isModuleReady,
        disabled,
        debouncedSearchText,
        sortField,
        sortType,
        entities: campaigns,
        entitiesFromStore: campaignsFromStore,
        selectedPickerType,
        selectedDimension: selectedCampaignDimension,
    });

    useEffect(() => {
        if (!forceReloadFlag) {
            return;
        }

        reset();
        reloadWrapper();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceReloadFlag]);

    // Here we manually are throwing event because can't use DOM due debouncing
    const mountRef = useRef();
    useEffect(() => {
        if (!mountRef.current) {
            return;
        }
        gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
            ...basicEditSearchFieldEvent,
            value: `Edit ${debouncedSearchText}`,
        });
        // eslint-disable-next-line
    }, [debouncedSearchText]);
    // Here we manually are throwing event because can't use DOM due to portal
    useEffect(() => {
        if (!mountRef.current) {
            mountRef.current = true;
            return;
        }
        gtmTracker.trackEvent(GTM_EVENTS.USABILITY, { ...basicSortEvent, value: `Sort by ${sortField} ${sortType}` });
        // eslint-disable-next-line
    }, [sortField, sortType]);

    return (
        <EntityPickerLayout
            status={status}
            disabled={disabled}
            onSearch={setSearchText}
            searchText={searchText}
            hasSort={true}
            sortType={sortType}
            sortField={sortField}
            sortOptions={sortFieldsOptions}
            onSelectSortField={handleOnSelectSortField}
            onSelectSortType={handleOnSelectSortType}
            sortTooltip={sortTooltip}
            listItems={campaigns}
            totalItems={totalCampaignsByDimension[selectedCampaignDimension] ?? 0}
            loadNextPage={loadNextPage}
            isItemLoaded={isItemLoaded}
            virtualizationState={debouncedSearchText}
            paginationStatus={paginationStatus}
            selectedItem={selectedItem}
            isSelectedItem={(item, selected) => item?.id === selected}
            loadingListItemComponent={CampaignContentLoader}
            getItemSize={getItemSize}
            getItemKey={item => item?.id}
            entity={ENTITY_TYPE.CAMPAIGN}
            selectedItemType={ENTITY.CAMPAIGN}
            onEditEntity={handleEditCampaign}
            isItemEditable={isCampaignEditFormDisabled}
            onHeightResolved={onHeightResolved}
            listItemComponent={CampaignPickerListItem}
            reloadWrapper={reloadWrapper}
        />
    );
};

export default CampaignPicker;
